import { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { m } from "framer-motion";
import { useAuth } from "../../hooks/useAuth";
import { supabase } from "../../lib/supabase";
import { getGuestCart } from "../../lib/guestSessionUtils";
import { onCartChanged } from "../../lib/cartEvents";
import { logger } from "../../utils/logger";
import { useCartModal } from "../../contexts/CartModalContext";

/**
 * Floating Cart Button Component
 *
 * Professional floating cart indicator that follows you everywhere.
 * Desktop: Fixed top-right (viewport-relative, always visible) | Mobile: Fixed bottom-center
 * Shows total quantity of items in cart.
 *
 * Features:
 * - Real-time cart count updates
 * - Desktop and mobile variants
 * - Smooth animations
 * - Theme-aware styling
 * - Accessibility compliant (ARIA, keyboard navigation, 44px touch targets)
 * - Performance optimized (memoized values and callbacks)
 * - Respects prefers-reduced-motion
 */
const FloatingCartButton = () => {
  const { user } = useAuth();
  const location = useLocation();
  const { toggleCart } = useCartModal();
  const [cartCount, setCartCount] = useState<number>(0);

  // Fetch cart count (total quantity, not unique items)
  const fetchCartCount = useCallback(async () => {
    try {
      let count = 0;

      if (user) {
        // Authenticated user - fetch from database
        const { data, error } = await supabase
          .from("cart_items")
          .select("quantity")
          .eq("user_id", user.id);

        if (error) throw error;
        count = (data || []).reduce(
          (sum: number, item: { quantity?: number }) =>
            sum + (item.quantity || 0),
          0,
        );
      } else {
        // Guest user - fetch from localStorage
        const guestCart = getGuestCart() as Array<{ quantity?: number }>;
        count = guestCart.reduce(
          (sum: number, item: { quantity?: number }) =>
            sum + (item.quantity || 0),
          0,
        );
      }

      setCartCount(count);
    } catch (error) {
      logger.error("Error fetching cart count:", error);
      setCartCount(0);
    }
  }, [user]);

  // Initial fetch and listen for cart changes
  useEffect(() => {
    fetchCartCount();

    // Listen for cart change events
    const cleanup = onCartChanged(() => {
      fetchCartCount();
    });

    // Real-time subscription for authenticated users
    let channel: ReturnType<typeof supabase.channel> | null = null;
    if (user) {
      channel = supabase
        .channel(`floating-cart-${user.id}`)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "cart_items",
            filter: `user_id=eq.${user.id}`,
          },
          () => {
            fetchCartCount();
          },
        )
        .subscribe();
    }

    // Listen for guest cart updates
    const handleGuestCartUpdate = () => {
      if (!user) {
        fetchCartCount();
      }
    };
    window.addEventListener("guestCartUpdated", handleGuestCartUpdate);

    return () => {
      cleanup();
      if (channel) {
        supabase.removeChannel(channel);
      }
      window.removeEventListener("guestCartUpdated", handleGuestCartUpdate);
    };
  }, [user, fetchCartCount]);

  // Memoized values
  const isMenuPage = useMemo(() => {
    return location.pathname === "/menu";
  }, [location.pathname]);

  const isEmpty = useMemo(() => cartCount === 0, [cartCount]);

  const prefersReducedMotion = useMemo(() => {
    return (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }, []);

  const cartLabel = useMemo(() => {
    if (isEmpty) return "View cart (empty)";
    return `View cart with ${cartCount} ${cartCount === 1 ? "item" : "items"}`;
  }, [isEmpty, cartCount]);

  const badgeText = useMemo(() => {
    return cartCount > 99 ? "99+" : String(cartCount);
  }, [cartCount]);

  // Only render on menu page
  if (!isMenuPage) {
    return null;
  }

  // Button content to be rendered via Portal
  const buttonContent = (
    <>
      {/* Desktop Version - Bottom Right (Fixed, always visible, transparent, icon-only) */}
      <m.div
        className="hidden md:flex fixed bottom-6 right-6 z-[99999] pointer-events-auto"
        style={{ position: "fixed" }}
        initial={
          prefersReducedMotion ? undefined : { opacity: 0, scale: 0.8, y: 20 }
        }
        animate={
          prefersReducedMotion ? undefined : { opacity: 1, scale: 1, y: 0 }
        }
        exit={
          prefersReducedMotion ? undefined : { opacity: 0, scale: 0.8, y: 20 }
        }
        transition={
          prefersReducedMotion
            ? { duration: 0 }
            : { type: "spring", stiffness: 300, damping: 25 }
        }
      >
        <button
          onClick={toggleCart}
          className="group relative flex items-center justify-center w-14 h-14 rounded-full bg-[var(--bg-elevated)]/60 backdrop-blur-xl border border-[var(--border-default)]/30 text-[var(--text-main)] shadow-lg shadow-black/10 hover:bg-[var(--bg-elevated)]/80 hover:border-[var(--border-default)]/50 hover:shadow-xl hover:shadow-black/15 transition-all duration-300 min-h-[56px] min-w-[56px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2"
          aria-label={cartLabel}
        >
          {/* Cart Icon */}
          <m.svg
            className="w-6 h-6 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
            whileHover={
              prefersReducedMotion
                ? undefined
                : { rotate: [0, -10, 10, -10, 0] }
            }
            transition={
              prefersReducedMotion ? { duration: 0 } : { duration: 0.5 }
            }
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"
            />
          </m.svg>

          {/* Cart Count Badge - Only show when not empty */}
          {!isEmpty && (
            <m.span
              className="absolute top-0 right-0 bg-gradient-to-br from-[var(--color-red)] to-[var(--color-red)]/90 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center shadow-lg ring-2 ring-white"
              aria-label={`${cartCount} items in cart`}
              initial={prefersReducedMotion ? undefined : { scale: 0 }}
              animate={prefersReducedMotion ? undefined : { scale: 1 }}
              transition={
                prefersReducedMotion
                  ? { duration: 0 }
                  : { type: "spring", stiffness: 500, damping: 30 }
              }
              whileHover={prefersReducedMotion ? undefined : { scale: 1.15 }}
            >
              {badgeText}
            </m.span>
          )}
        </button>
      </m.div>

      {/* Mobile Version - Bottom Right (Fixed, always visible, transparent, icon-only) */}
      <m.div
        className="md:hidden fixed bottom-6 right-6 z-[99999] pointer-events-auto"
        style={{ position: "fixed" }}
        initial={
          prefersReducedMotion ? undefined : { opacity: 0, scale: 0.8, y: 20 }
        }
        animate={
          prefersReducedMotion ? undefined : { opacity: 1, scale: 1, y: 0 }
        }
        exit={
          prefersReducedMotion ? undefined : { opacity: 0, scale: 0.8, y: 20 }
        }
        transition={
          prefersReducedMotion
            ? { duration: 0 }
            : { type: "spring", stiffness: 300, damping: 25 }
        }
      >
        <button
          onClick={toggleCart}
          className="group relative flex items-center justify-center w-14 h-14 rounded-full bg-[var(--bg-elevated)]/60 backdrop-blur-xl border border-[var(--border-default)]/30 text-[var(--text-main)] shadow-lg shadow-black/10 hover:bg-[var(--bg-elevated)]/80 hover:border-[var(--border-default)]/50 hover:shadow-xl hover:shadow-black/15 active:scale-95 transition-all duration-300 min-h-[56px] min-w-[56px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2"
          aria-label={cartLabel}
        >
          {/* Cart Icon */}
          <m.svg
            className="w-6 h-6 flex-shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2.5}
            whileTap={prefersReducedMotion ? undefined : { scale: 0.9 }}
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"
            />
          </m.svg>

          {/* Cart Count Badge - Only show when not empty */}
          {!isEmpty && (
            <m.span
              className="absolute top-0 right-0 bg-gradient-to-br from-[var(--color-red)] to-[var(--color-red)]/90 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center shadow-lg ring-2 ring-white"
              aria-label={`${cartCount} items in cart`}
              initial={prefersReducedMotion ? undefined : { scale: 0 }}
              animate={prefersReducedMotion ? undefined : { scale: 1 }}
              transition={
                prefersReducedMotion
                  ? { duration: 0 }
                  : { type: "spring", stiffness: 500, damping: 30 }
              }
              whileTap={prefersReducedMotion ? undefined : { scale: 1.1 }}
            >
              {badgeText}
            </m.span>
          )}
        </button>
      </m.div>
    </>
  );

  // Render via Portal to document.body to escape filter/transform containers
  // This ensures position: fixed works relative to viewport, not parent container
  return typeof window !== "undefined" && document.body
    ? createPortal(buttonContent, document.body)
    : null;
};

export default FloatingCartButton;
